import enGB from 'date-fns/locale/en-GB';
import format from 'date-fns/format';

export const DATE_ISO = "yyyy-MM-dd'T00:00:00Z'";
export const DATE_LONG = 'iiii do MMMM yyyy';
export const DATE_MEDIUM_LONG = 'iii d MMMM yyyy';
export const DATE_MEDIUM = 'do MMMM yyyy';
export const DATE_SHORT_SLASH = 'dd/MM/yyyy';
export const DATE_SHORT_FULL_STOP = 'dd.MM.yyyy';
export const DATE_NATIVE = 'yyyy-MM-dd';
export const TIME = 'k:mm';
export const DAY_MONTH = 'dd MMM';

const withNativeDate = (date?: Date | string): Date | null => {
  if (!date) return null;

  if (date instanceof Date) return date;

  return new Date(date);
};

export const formatSecondsToMinutes = (seconds: string): string => {
  const value = parseInt(seconds, 10);

  if (!value) return '';

  if (value < 59) return `${seconds} second`;

  return `${Math.floor(value / 60)} minute`;
};

export const makeFormat = (
  dateFormat: string,
  date?: string | Date
): string => {
  const asDate = withNativeDate(date);

  return asDate && format(asDate, dateFormat, { locale: enGB });
};

export function formatDate(time: string, date: Date): Date {
  const startTime = time.split('').splice(0, 2);

  const formattedTime =
    startTime[0] === '0' ? startTime.splice(1, 1).join('') : startTime.join('');

  return new Date(date.setHours(parseInt(formattedTime), 0, 0));
}

export const formatDateISO = (date?: string | Date) =>
  makeFormat(DATE_ISO, date);

export const formatDateLong = (date?: string | Date) =>
  makeFormat(DATE_LONG, date);

export const formatDateMedium = (date?: string | Date) =>
  makeFormat(DATE_MEDIUM, date);

export const formatDateMediumLong = (date?: string | Date) =>
  makeFormat(DATE_MEDIUM_LONG, date);

export const formatDateShort = (
  date?: string | Date,
  fullStopFormat: boolean = false
) => makeFormat(fullStopFormat ? DATE_SHORT_FULL_STOP : DATE_SHORT_SLASH, date);

export const formatDateNative = (date?: string | Date) =>
  makeFormat(DATE_NATIVE, date);

export const formatTime = (date?: string | Date) => makeFormat(TIME, date);

export const formatDayDate = (date?: string | Date) =>
  makeFormat(DAY_MONTH, date);

export const formatTimeString = (timeString: string): string =>
  formatTime(new Date(`2020-01-01T${timeString}Z`));

export const formatDaysToSeconds = (days: number): number =>
  days * 24 * 60 * 60;

export const formatSecondsToDays = (seconds?: number): string | null => {
  if (!seconds) return null;

  const days = seconds / 60 / 60 / 24;

  return days.toString();
};

export function getRandomDate(
  startDate: Date | string,
  endDate: Date | string
): Date {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    throw new Error('Invalid date input');
  }

  if (start.getTime() > end.getTime()) {
    throw new Error('Start date must be before end date');
  }

  const startTimestamp = start.getTime();
  const endTimestamp = end.getTime();

  const randomTimestamp =
    Math.floor(Math.random() * (endTimestamp - startTimestamp + 1)) +
    startTimestamp;

  return new Date(randomTimestamp);
}
